import { useCallback, useMemo } from 'react'
import { assoc } from 'ramda'
import { sortByTitle } from 'app/common/lib/sort'

import type { RequestSalePointsParams, SalePoint } from 'app/common/types/sale-points'
import { useMachineForm, useMachineValidate } from 'app/dataProviders/VendingDataProvider'
import { useSalePoints } from 'app/dataProviders/salePoint/hooks/useSalePoints'

type Result = {
  salePoints: Array<SalePoint>
  loadingSalePoints: boolean
  salePointSelection: Array<string>
  changeCurrentSalePoint: (values: Array<string>) => void
}

export function useActualSalePoints(): Result {
  const [{ orgId, buildingId, salePointId }, { update }] = useMachineForm()
  const [, , resetErrors] = useMachineValidate()

  const orgSelected = Boolean(orgId)

  const salePointsParams = useMemo<RequestSalePointsParams>(() => {
    const params: RequestSalePointsParams = {
      orgId: String(orgId) as RequestSalePointsParams['orgId'],
    }
    if (buildingId) {
      params.buildingId = String(buildingId) as RequestSalePointsParams['buildingId']
    }
    return params
  }, [orgId, buildingId])

  const { data: salePointsData, loading: loadingSalePointsRaw } = useSalePoints({
    params: salePointsParams,
    autoload: orgSelected,
    autoupdate: orgSelected,
    initialLoading: false,
  })

  const salePoints = useMemo(() => {
    const actualSalePoints = (salePointsData || []).filter((salePoint) => {
      return (
        !salePoint.deleted &&
        typeof salePoint.vendingMachinesCounter === 'number' &&
        salePoint.vendingMachinesCounter < 1
      )
    })
    return sortByTitle(actualSalePoints)
  }, [salePointsData])

  const loadingSalePoints = orgSelected ? loadingSalePointsRaw : false

  const salePointSelection = useMemo(
    () => (salePointId ? [String(salePointId)] : []),
    [salePointId],
  )

  const changeCurrentSalePoint = useCallback(
    ([salePointId_]: Array<string>) => {
      resetErrors()
      update(assoc('salePointId', parseInt(salePointId_, 10) || 0))
    },
    [update, resetErrors],
  )

  return {
    salePoints,
    loadingSalePoints,
    salePointSelection,
    changeCurrentSalePoint,
  }
}
